import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Search, Database, Clock, CheckCircle, AlertCircle, Code } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * ReasoningTrace Component
 *
 * Displays the AI's reasoning process - what tools were called,
 * with what parameters, and what results were returned.
 *
 * This addresses enterprise audit concerns by making AI decisions transparent.
 */
const ReasoningTrace = ({ toolResults, className }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedTools, setExpandedTools] = useState({});

  if (!toolResults || toolResults.length === 0) {
    return null;
  }

  const toggleTool = (index) => {
    setExpandedTools(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Format parameter values for display
  const formatParamValue = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Get human-readable tool name
  const getToolDisplayName = (toolName) => {
    const nameMap = {
      'get_goals_progress': 'Goals Progress',
      'get_opportunities': 'Opportunities',
      'search_opportunities': 'Search Opportunities',
      'get_parts': 'Parts',
      'search_parts': 'Search Parts',
      'get_forecasts': 'Forecasts',
      'get_summary_stats': 'Summary Statistics',
      'get_my_summary': 'My Summary',
      'get_alerts': 'Alerts',
      'get_claims': 'Claims',
      'search_claims': 'Search Claims',
      'get_segments_breakdown': 'Segments Breakdown',
      'get_parts_by_dates': 'Parts by Date',
      'get_opportunity_parts': 'Opportunity Parts',
      'get_pipeline_forecast': 'Pipeline Forecast',
      'get_programs': 'Programs',
      'get_data_quality': 'Data Quality',
      'search_decision_history': 'Decision History',
      'get_file_context': 'File Context',
    };
    return nameMap[toolName] || toolName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get result summary
  const getResultSummary = (result) => {
    if (!result) return 'No data';
    if (result.error) return `Error: ${result.error}`;
    if (result.count !== undefined) return `${result.count} record${result.count !== 1 ? 's' : ''} found`;
    if (result.entities?.length) return `${result.entities.length} item${result.entities.length !== 1 ? 's' : ''} returned`;
    if (result.summary) return result.summary;
    return 'Completed';
  };

  // Determine result status
  const getResultStatus = (result) => {
    if (!result) return 'empty';
    if (result.error) return 'error';
    if (result.count === 0 || result.entities?.length === 0) return 'empty';
    return 'success';
  };

  return (
    <div className={cn('mt-2', className)}>
      {/* Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors group"
      >
        {isExpanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        <Search className="h-3 w-3" />
        <span>How I found this</span>
        <span className="text-gray-400 group-hover:text-gray-500">
          ({toolResults.length} query{toolResults.length !== 1 ? 'ies' : 'y'})
        </span>
      </button>

      {/* Expanded Trace */}
      {isExpanded && (
        <div className="mt-2 space-y-2 pl-4 border-l-2 border-gray-200">
          {toolResults.map((tr, index) => {
            const toolName = tr.tool;
            const params = tr.parameters || {};
            const result = tr.result;
            const status = getResultStatus(result);
            const isToolExpanded = expandedTools[index];

            return (
              <div
                key={index}
                className="bg-gray-50 rounded-lg p-2 text-xs"
              >
                {/* Tool Header */}
                <button
                  onClick={() => toggleTool(index)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <div className="flex items-center gap-2">
                    <Database className="h-3 w-3 text-gray-400" />
                    <span className="font-medium text-gray-700">
                      {getToolDisplayName(toolName)}
                    </span>
                    {status === 'success' && (
                      <CheckCircle className="h-3 w-3 text-green-500" />
                    )}
                    {status === 'error' && (
                      <AlertCircle className="h-3 w-3 text-red-500" />
                    )}
                    {status === 'empty' && (
                      <span className="text-gray-400">(no results)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <span>{getResultSummary(result)}</span>
                    {isToolExpanded ? (
                      <ChevronDown className="h-3 w-3" />
                    ) : (
                      <ChevronRight className="h-3 w-3" />
                    )}
                  </div>
                </button>

                {/* Expanded Tool Details */}
                {isToolExpanded && (
                  <div className="mt-2 pt-2 border-t border-gray-200 space-y-2">
                    {/* Parameters */}
                    {Object.keys(params).length > 0 && (
                      <div>
                        <div className="flex items-center gap-1 text-gray-500 mb-1">
                          <Code className="h-3 w-3" />
                          <span className="font-medium">Parameters:</span>
                        </div>
                        <div className="bg-white rounded p-2 font-mono text-[10px] space-y-0.5">
                          {Object.entries(params).map(([key, value]) => (
                            <div key={key} className="flex">
                              <span className="text-purple-600 min-w-[100px]">{key}:</span>
                              <span className="text-gray-700">{formatParamValue(value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Result Summary */}
                    {result && !result.error && (
                      <div>
                        <div className="flex items-center gap-1 text-gray-500 mb-1">
                          <Database className="h-3 w-3" />
                          <span className="font-medium">Result:</span>
                        </div>
                        <div className="bg-white rounded p-2 text-[10px] space-y-0.5">
                          {result.count !== undefined && (
                            <div className="flex">
                              <span className="text-gray-500 min-w-[100px]">Records:</span>
                              <span className="text-gray-700 font-medium">{result.count}</span>
                            </div>
                          )}
                          {result.response_type && (
                            <div className="flex">
                              <span className="text-gray-500 min-w-[100px]">Type:</span>
                              <span className="text-gray-700">{result.response_type}</span>
                            </div>
                          )}
                          {result.entity_type && (
                            <div className="flex">
                              <span className="text-gray-500 min-w-[100px]">Entity:</span>
                              <span className="text-gray-700">{result.entity_type}</span>
                            </div>
                          )}
                          {result.total_pipeline_value && (
                            <div className="flex">
                              <span className="text-gray-500 min-w-[100px]">Total Value:</span>
                              <span className="text-[#07689f] font-medium">{result.total_pipeline_value}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Error Display */}
                    {result?.error && (
                      <div className="bg-red-50 text-red-700 rounded p-2 text-[10px]">
                        {result.error}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Data Source Badge */}
          <div className="flex items-center gap-1.5 text-[10px] text-gray-400 pt-1">
            <Database className="h-2.5 w-2.5" />
            <span>Data sourced from your AutoFlo database</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReasoningTrace;
